import { useEffect, useState } from "react";
import { Loader2, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { opsApi } from "../../lib/api";

const KIND_LABEL = { quantity: "Quantity", add: "Add", remove: "Remove" };
const KIND_TONE = {
  quantity: "border-sky-200 bg-sky-50 text-sky-700",
  add: "border-emerald-200 bg-emerald-50 text-emerald-700",
  remove: "border-rose-200 bg-rose-50 text-rose-700",
};

/** "This CO modifies project scope. Update Current FRS?" — the system proposes, the PE decides.
 *  Either the FRS takes a revision stamped with the CO number, or an FRS Update task is raised. */
export default function FrsSyncDialog({ projectId, co, onClose, onDone }) {
  const [data, setData] = useState(null);
  const [accept, setAccept] = useState({});
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [place, setPlace] = useState({});   // line_id -> room key "building|floor|room"

  useEffect(() => {
    opsApi.coFrsProposals(projectId, co.id).then((d) => {
      setData(d);
      setAccept(Object.fromEntries((d.proposals || []).map((p) => [p.id, true])));
      // A line whose product already sits in exactly one room is placed there automatically.
      const auto = {};
      for (const u of d.unplaced || []) {
        const hits = (u.rooms || []).filter((r) => r.match);
        if (hits.length === 1) auto[u.line_id] = hits[0].key;
      }
      setPlace(auto);
    }).catch(() => toast.error("Could not work out what this change order does to the FRS"));
  }, [projectId, co.id]);

  const placements = (data?.unplaced || []).flatMap((u) => {
    const r = (u.rooms || []).find((x) => x.key === place[u.line_id]);
    return r ? [{ line_id: u.line_id, building: r.building, floor: r.floor, room_number: r.room_number, qty: u.qty, product: u.description }] : [];
  });
  const stillUnplaced = (data?.unplaced || []).filter((u) => !place[u.line_id]);

  const run = async (mode) => {
    setBusy(true);
    try {
      const res = await opsApi.coFrsSync(projectId, co.id, {
        mode, note, placements,
        accept: [...Object.entries(accept).filter(([, v]) => v).map(([k]) => k),
                 ...placements.map((p) => `${p.line_id}:new:${p.room_number}`)],
      });
      toast.success(mode === "task" ? "FRS Update task raised for the PE"
        : mode === "not_applicable" ? "Marked as no scope change"
          : `FRS revision written — ${res.applied.quantity} quantity, ${res.applied.add} added, `
            + `${res.applied.remove} removed`);
      onDone(res.change_order);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "The FRS could not be synced");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
         onClick={onClose}>
      <div className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-xl border border-border bg-card text-card-foreground p-5 shadow-xl"
           onClick={(e) => e.stopPropagation()} data-testid="ops-co-frs-sync-dialog">
        <h3 className="text-base font-semibold text-slate-900">
          {co.number} modifies project scope — update the Current FRS?
        </h3>
        <p className="mt-1 text-xs text-slate-500">
          Every accepted row writes an FRS revision stamped with {co.number}, so the room finish
          schedule and the change order can never disagree.
        </p>

        {!data ? (
          <div className="flex items-center gap-2 py-8 text-xs text-slate-500">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Working out the room changes…
          </div>
        ) : (
          <>
            <div className="mt-3 flex-1 overflow-y-auto rounded-lg border border-slate-200">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="w-8 px-2 py-1.5" />
                    <th className="px-3 py-1.5 text-left">Change</th>
                    <th className="px-3 py-1.5 text-left">Room</th>
                    <th className="px-3 py-1.5 text-left">Product</th>
                    <th className="px-3 py-1.5 text-right">Qty</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {data.proposals.length === 0 && (
                    <tr><td colSpan={5} className="px-3 py-5 text-center text-slate-500">
                      Nothing on this change order points at a room.
                    </td></tr>
                  )}
                  {data.proposals.map((p) => (
                    <tr key={p.id} data-testid={`ops-co-frs-proposal-${p.id}`}>
                      <td className="px-2 py-1.5">
                        <input type="checkbox" checked={!!accept[p.id]}
                               onChange={() => setAccept((a) => ({ ...a, [p.id]: !a[p.id] }))}
                               data-testid={`ops-co-frs-accept-${p.id}`} />
                      </td>
                      <td className="px-3 py-1.5">
                        <span className={`rounded-full border px-1.5 py-0.5 text-[10px] ${KIND_TONE[p.kind]}`}>
                          {KIND_LABEL[p.kind]}
                        </span>
                        {p.mobilized && (
                          <span className="ml-1 text-[10px] text-amber-600">mobilised</span>
                        )}
                      </td>
                      <td className="px-3 py-1.5">
                        {[p.room.building, p.room.floor, p.room.room_number].filter(Boolean).join(" · ")}
                      </td>
                      <td className="px-3 py-1.5">{p.product}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">
                        {p.qty_from} → {p.qty_to}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {data.unplaced?.length > 0 && (
              <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200"
                   data-testid="ops-co-frs-unplaced">
                <p className="flex items-center gap-1.5 font-medium">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  {data.unplaced.length} line(s) have no room yet — pick where each one goes
                </p>
                <p className="mt-1 text-[11px] opacity-80">
                  Rooms that already carry the same product are listed first (★), then rooms with the same scope.
                  Leave a line blank to raise an FRS Update task for it instead.
                </p>
                <ul className="mt-2 space-y-1.5">
                  {data.unplaced.map((u) => (
                    <li key={u.line_id} className="flex flex-wrap items-center gap-2" data-testid={`ops-co-frs-place-${u.line_id}`}>
                      <span className="min-w-0 flex-1 truncate">
                        <b>{u.qty}×</b> {u.description || u.item_number}{u.scope_code ? <span className="opacity-70"> · {u.scope_code}</span> : null}
                      </span>
                      <select value={place[u.line_id] || ""} data-testid={`ops-co-frs-place-select-${u.line_id}`}
                              onChange={(e) => setPlace((p) => ({ ...p, [u.line_id]: e.target.value }))}
                              className="h-7 max-w-[260px] rounded-md border border-amber-300 bg-white px-2 text-xs text-slate-800 dark:border-amber-700 dark:bg-slate-900 dark:text-slate-100">
                        <option value="">Choose a room…</option>
                        {(u.rooms || []).map((r) => (
                          <option key={r.key} value={r.key}>
                            {r.match ? "★ " : ""}{[r.building, r.floor, r.room_number].filter(Boolean).join(" · ")}{r.room_name ? ` — ${r.room_name}` : ""}{!r.match && r.same_scope ? " (same scope)" : ""}
                          </option>
                        ))}
                      </select>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2}
                      className="mt-3 text-sm" data-testid="ops-co-frs-note"
                      placeholder="Note for the FRS revision (optional)" />

            <div className="mt-4 flex flex-wrap justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={onClose}>Cancel</Button>
              <Button variant="outline" size="sm" disabled={busy} onClick={() => run("not_applicable")}
                      data-testid="ops-co-frs-na">No scope change</Button>
              <Button variant="outline" size="sm" disabled={busy} onClick={() => run("task")}
                      data-testid="ops-co-frs-task">Raise an FRS Update task</Button>
              <Button size="sm" disabled={busy || (!data.proposals.length && !placements.length)} onClick={() => run("sync")}
                      data-testid="ops-co-frs-sync">
                {busy && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
                Sync to the FRS{placements.length ? ` (+${placements.length} placed)` : ""}
              </Button>
            </div>
            {stillUnplaced.length > 0 && data.proposals.length + placements.length > 0 && (
              <p className="mt-2 text-right text-[11px] text-slate-500">
                {stillUnplaced.length} line(s) without a room will stay off the FRS — raise a task for them after syncing.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
